import {
  colors,
  ImpButton,
  ImpContainer,
  ImpOtpField,
  ImpSpacer,
  ImpText,
  useSnackbar,
} from '@imprint-payments/imprint-ui';
import { useEffect, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { createPin } from '../../api/createPin/createPin';
import { loadHome } from '../../api/loadHome/loadHome';
import { useCreatePin } from '../../api/pin/createPin';
import { useForgotPin } from '../../api/pin/forgotPin';
import { verifyPin } from '../../api/pin/verifyPin';
import { setLoading } from '../../app/applicationScreenSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import {
  setLockOutMessage,
  setLoginFlow,
  setPinError,
} from '../../app/loginSlice';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import { FORM_BUTTON_WIDTH } from '../../assets/consts/const';
import { ANALYTICS } from '../../firebase/firebase';
import { isValidPin, ValidationResponse } from '../../utils/validateUtils';
import Spinner from '../spinner/Spinner';
import { handleLoadHomeSuccess } from './loadHomeHandlers';

interface PINEntryProps {
  newPin?: boolean;
  onComplete?: () => void;
}

export default function PINEntry({ newPin, onComplete }: PINEntryProps) {
  const { loginState, dispatch, appScreenState, language, consumerInfo } =
    useImpState();
  const [input, setInput] = useState('');
  const [pinToConfirm, setPinToConfirm] = useState('');
  const [curr, setCurr] = useState(0);
  const [error, setError] = useState(ValidationResponse.empty);
  const [confirmingPin, setConfirmingPin] = useState(false);
  const [invalidPinCriteria, setInvalidPinCriteria] = useState(false);
  const [clearInput, setClearInput] = useState(false);

  const [showError, setShowError] = useState(false);
  const loginFlow = useAppSelector((state) => state.loginSelector.loginFlow);

  const createPinMutation = useCreatePin();
  const forgotPinMutation = useForgotPin();

  const { displayTemporarySnackbar } = useSnackbar();

  useEffect(() => {
    if (![ValidationResponse.valid, ValidationResponse.empty].includes(error)) {
      ANALYTICS.logEvent(EVENT.CONFIRM_PIN_TRY_AGAIN_VIEWED);
      setShowError(true);
    }
  }, [error]);

  useEffect(() => {
    // clear error on mount
    dispatch(setLockOutMessage(''));
    dispatch(setPinError(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowError(loginFlow.pinError);
    if (!newPin) {
      if (loginFlow.pinError) {
        ANALYTICS.logEvent(EVENT.ENTER_PIN_TRY_AGAIN_VIEWED);
        setError(ValidationResponse.invalidPin);
      } else {
        setError(ValidationResponse.empty);
      }
    } else {
      if (loginFlow.pinError) {
        setError(ValidationResponse.invalidPin);
      } else {
        setError(ValidationResponse.valid);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginFlow.pinError]);

  useEffect(() => {
    if (confirmingPin) {
      ANALYTICS.logEvent(EVENT.CONFIRM_PIN_VIEWED);
    }
  }, [confirmingPin]);

  useEffect(() => {
    if (!newPin && loginFlow.pinError && curr > 0) {
      dispatch(setLoginFlow({ ...loginFlow, pinError: false }));
    }
    if (
      newPin &&
      [
        ValidationResponse.invalidPin,
        ValidationResponse.invalidPinNoMatch,
      ].includes(error) &&
      curr > 0
    ) {
      setError(ValidationResponse.empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, pinToConfirm]);

  function handleError(r: ValidationResponse) {
    setError(r);
  }

  function focusOnInputBox(inputBox: number) {
    setCurr(inputBox);
    const input = document.getElementById(inputBox.toString());
    if (input != null) {
      input.focus();
    }
  }

  function clearPinToConfirm() {
    // clear pin to confirm
    setPinToConfirm('');

    // set input boxes
    const inputBoxes = document.querySelectorAll('input');
    inputBoxes.forEach((inputBox) => {
      inputBox.value = '';
    });

    focusOnInputBox(0);
  }

  function clearError() {
    setError(ValidationResponse.empty);
  }

  function handleChange(s: string) {
    setClearInput(false);
    if (input !== '') {
      setShowError(false);
      setInvalidPinCriteria(false);
    }
    setClearInput(false);
    setInput(s);
  }

  const handleInputFull = (s: string) => {
    if (!newPin) {
      ANALYTICS.logEvent(EVENT.ENTER_PIN_SUBMITTED);
      checkPinWithBE(s);
    } else if (confirmingPin) {
      ANALYTICS.logEvent(EVENT.CONFIRM_PIN_SUBMITTED);
      comparePins();
    } else {
      ANALYTICS.logEvent(EVENT.CREATE_PIN_SUBMITTED);
      validatePIN(s);
    }
  };

  function validatePIN(p: string) {
    const pinValidity = isValidPin(p);
    if (pinValidity !== ValidationResponse.valid) {
      ANALYTICS.logEvent(EVENT.CREATE_PIN_INVALID_PIN_VIEWED);
      handleError(pinValidity);
      setInvalidPinCriteria(true);
    } else {
      setPinToConfirm(p);
      setError(pinValidity);
      setConfirmingPin(true);
      setClearInput(true);
    }
  }

  const handleVerifyPinSuccess = () => {
    dispatch(setLoginFlow({ ...loginFlow, pinError: false }));
    loadHome({
      onSuccess: () => {
        // how do we know this is false at this point?
        handleLoadHomeSuccess({
          isReapplication: false,
        });
        dispatch(setLoading(false));
      },
      onFail: handleLoadHomeFail,
    });
  };

  // function for existing pin
  function checkPinWithBE(p: string) {
    dispatch(setLoading(true));
    verifyPin({
      pin: p,
      onSuccess: handleVerifyPinSuccess,
      onIncorrectEntry: (message: string) => {
        dispatch(setLoading(false));
        dispatch(setPinError(true));
        dispatch(setLockOutMessage(message));
        setShowError(true);
      },
      onError: () => {
        dispatch(setLoading(false));
        dispatch(setPinError(true));
        setShowError(true);
        displayTemporarySnackbar({ text: language.somethingWentWrong });
      },
      onLockout: (message: string) => {
        dispatch(setLoading(false));
        dispatch(setScreen(SCREEN.VERIFICATION_FAILED));
        dispatch(setLockOutMessage(message));
      },
    });
  }

  // functions for create pin
  function comparePins() {
    if (input === pinToConfirm) {
      handleMatchingPins();
    } else {
      handleNonMatchingPins();
    }
  }

  function handleLoadHomeFail() {
    displayTemporarySnackbar({ text: language.somethingWentWrong });
  }

  function handleCreatePinSuccess() {
    if (onComplete) {
      // SDK onboarding flow
      dispatch(setLoading(false));
      onComplete();
      return;
    }

    loadHome({
      onSuccess: () => {
        handleLoadHomeSuccess({
          isReapplication: false,
        });
        dispatch(setLoading(false));
      },
      onFail: handleLoadHomeFail,
    });
  }

  function handleCreatePinFail() {
    dispatch(setLoading(false));
    setClearInput(true);
    displayTemporarySnackbar({ text: language.somethingWentWrong });
  }

  function handleMatchingPins() {
    setError(ValidationResponse.valid);
    dispatch(setLoading(true));
    if (loginState.multiCardUser) {
      // POST /v1/consumer/pin
      // Multi-card user reset PIN
      createPin({
        pin: input,
        onSuccess: handleCreatePinSuccess,
        onFail: handleCreatePinFail,
      });
    } else {
      // POST /v1/account/pin
      // New user create new PIN
      createPinMutation.mutate(
        {
          consumerUUID: consumerInfo.consumerUUID,
          newPin: input,
          oldPin: '',
        },
        { onSuccess: handleCreatePinSuccess, onError: handleCreatePinFail }
      );
    }
  }

  function handleNonMatchingPins() {
    setError(ValidationResponse.invalidPinNoMatch);
    setShowError(true);
  }

  function handleForgotPinClick() {
    ANALYTICS.logEvent(EVENT.LOGIN_FORGOT_PIN_CLICKED);
    setClearInput(true);
    clearError();
    dispatch(setLoading(true));
    forgotPinMutation.mutate();
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <ImpSpacer height="xl" />
      <ImpText typography="headline3">
        {newPin
          ? confirmingPin
            ? language.reEnterYourPIN
            : language.createAPIN
          : language.enterYourPin}
      </ImpText>
      <ImpSpacer height="xs" />
      <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
        {newPin ? language.createPinSubtitle : language.enterYourImprintPin}
      </ImpText>
      <ImpSpacer height="xl" />
      <ImpOtpField
        numInputs={4}
        onChange={handleChange}
        onFull={handleInputFull}
        clearWithError={showError || invalidPinCriteria}
        clear={clearInput}
        mask={true}
      />
      <ImpSpacer height="xl" />
      {showError && (
        <ImpText color={colors.content.onSurfaceError}>
          {loginState.lockOutMessage}
        </ImpText>
      )}
      {error === ValidationResponse.invalidPinNoMatch && (
        <ImpText color={colors.content.onSurfaceError}>
          {language.makeSurePinsMatch}
        </ImpText>
      )}
      {newPin && (
        <ImpContainer width={FORM_BUTTON_WIDTH}>
          <ImpSpacer height="xl" />
          <ImpText
            typography="body3"
            color={
              error === ValidationResponse.invalidPin
                ? colors.content.onBackgroundError
                : '#5e5d5c'
            }
          >
            {language.pinCreationNote}
          </ImpText>
        </ImpContainer>
      )}
      {confirmingPin && !appScreenState.loading && (
        <>
          <ImpSpacer height="xl" />
          <ImpButton
            variant="secondary"
            buttonStyle="imprint"
            onClick={() => {
              if (
                confirmingPin &&
                [ValidationResponse.valid, ValidationResponse.empty].includes(
                  error
                )
              ) {
                ANALYTICS.logEvent(
                  EVENT.CONFIRM_PIN_TRY_AGAIN_RESET_PIN_CLICKED
                );
              } else if (confirmingPin) {
                ANALYTICS.logEvent(EVENT.CONFIRM_PIN_RESET_PIN_CLICKED);
              }
              setClearInput(true);
              clearError();
              clearPinToConfirm();
              setConfirmingPin(false);
            }}
          >
            {language.resetPin}
          </ImpButton>
        </>
      )}
      {!newPin && !appScreenState.loading && (
        <ImpButton
          variant="tertiary"
          buttonStyle="imprint"
          size="hug"
          onClick={handleForgotPinClick}
        >
          {language.forgotPin}
        </ImpButton>
      )}
      {appScreenState.loading && <Spinner />}
      <ImpSpacer height="2xl" />
    </div>
  );
}
